import { HttpClient } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { of, Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { ContractGridItem } from 'src/app/clinical/contracts/grid/models/contract-grid-item.model';
import { ContractGridLookups } from 'src/app/clinical/contracts/grid/models/contract-grid-lookups.model';
import { PerformanceEval } from 'src/app/clinical/contracts/grid/models/performance-eval.model';
import { APP_CONFIG, Settings } from 'src/app/config/settings';
import { WINDOW } from 'src/app/core/core.module';
import { combine } from 'src/app/shared/utilities/route';
import { FetchArgs, PagingTokenFixed } from 'src/app/shared/models/core';
import { fixDateFilters } from 'src/app/shared/utilities/interpolate-params';

@Injectable({
    providedIn: 'root'
})
export class ContractService {
    private readonly coreApiPath: string;

    constructor(
        @Inject(APP_CONFIG) settings: Settings,
        @Inject(WINDOW) private readonly window: Window,
        private readonly http: HttpClient
    ) {
        this.coreApiPath = combine(settings.coreAPIUrl, 'ayanova/');
    }

    search(args: FetchArgs): Observable<PagingTokenFixed<ContractGridItem[]>> {
        const url = combine(this.coreApiPath, 'clinical/contracts');
        fixDateFilters(args);
        return this.postPaged(url, args);
    }

    lookups(): Observable<ContractGridLookups> {
        const url = combine(this.coreApiPath, 'clinical/contracts/grid-lookups');
        return this.http.get<ContractGridLookups>(url);
    }

    accountManagerAutocomplete(searchText: string): Observable<string[]> {
        const url = combine(
            this.coreApiPath,
            `clinical/contracts/grid-lookups/account-managers?searchText=${searchText}`
        );
        return this.http.get<string[]>(url);
    }

    recruiterAutocomplete(searchText: string): Observable<string[]> {
        const url = combine(this.coreApiPath, `clinical/contracts/grid-lookups/recruiters?searchText=${searchText}`);
        return this.http.get<string[]>(url);
    }

    private getById(id: number): Observable<ContractGridItem> {
        const url = combine(this.coreApiPath, `clinical/contracts/${id.toString()}`);
        return this.http.get<ContractGridItem>(url);
    }

    performanceEvalFormsToProgress(evalContractIds: number[]): Observable<any> {
        if (!evalContractIds || !evalContractIds.length) {
            return of([]);
        }
        const url = combine(this.coreApiPath, 'PerformanceEval/performance-eval-forms');
        return this.http.put(url, { contractIds: evalContractIds });
    }

    getPerformanceEval(contractId: number): Observable<PerformanceEval> {
        const url = combine(this.coreApiPath, `PerformanceEval/GetPerformanceEvalByContractId/${contractId}`);
        return this.http.get<PerformanceEval>(url);
    }

    storePerformanceEvalForm(performanceEvalForm): Observable<PerformanceEval> {
        const url = combine(this.coreApiPath, 'PerformanceEval/StorePerformanceEvalForm');
        return this.http.post<PerformanceEval>(url, performanceEvalForm);
    }

    postPaged<T>(url: string, fetchArgs: FetchArgs): Observable<PagingTokenFixed<T[]>> {
        return this.http.post<PagingTokenFixed<T[]>>(url, fetchArgs);
    }

    downloadPerformanceEvalPdf(url: string, fileName: string): void {
        const doc = this.getFile(url);
        this.downloadFile(doc, fileName);
    }

    private getFile(url: string): Observable<{ file: Blob; url: string }> {
        return this.http.get(url, { responseType: 'blob' }).pipe(
            map((file) => {
                const url = this.window.window.URL.createObjectURL(file);
                return { file, url };
            })
        );
    }

    private downloadFile(value: Observable<{ file: Blob; url: string }>, fileName: string): void {
        value.subscribe((file) => {
            const downloadLink = this.window.document.createElement('a');
            downloadLink.download = fileName;
            downloadLink.href = file.url;
            downloadLink.click();
        });
    }
}
